<template>
<!-- Post marker -->
<span v-for="post in postsStore.postsArray" :key="post.id">
	<svg
		xmlns="http://www.w3.org/2000/svg"
		:width="marckWidth"
		:height="marckHeight"
		class="feather align-text-bottom post-marker"
		viewBox="0 0 16 16"
		:ref="post.id"
		@contextmenu.stop="(event) => showPostMarkerContextMenu(event, post)"
		@click="onClickPostMarker(post)"
	>
		<path :d="postSvgPath" transform="translate(3,9) scale(0.6)"/>
		<path
			d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"
			transform="translate(0,-8)"
			:fill="postMarkersColor[post.id]"
			stroke="black"
			stroke-width="0.3"
			stroke-linecap="round"
		/>
		<text
			x="100%"
			y="45"
			fill="black"
			transform="translate(0,2) scale(0.5)"
			text-anchor="middle"
		>
			{{ post.getMetric(postObj.metricsKeys.Eavg)?.value.toFixed(2) }}
		</text>
	</svg>
</span>

<post-context-menu
	:position="postContextMenuPosition"
	:visible="postContextMenuVisible"
	:post="postForContextMenu"
	:topShift="mapContainerEl.getBoundingClientRect().x"
	:leftShift="mapContainerEl.getBoundingClientRect().y"
	@removeLocation="removePostLocation"
/>
</template>


<script>
import { Post } from '@/models/post.js'
import {
	Marker,
	Popup,
} from '@maptiler/sdk';
import { usePostsStore } from '@/stores/PostsStore'
import PostContextMenu from "@/components/MapView/PostContextMenu";

export default {
	POSTS_SOURCE_ID: 'posts',

	inject: [ 'postSvgPath', ],

	components: {
		PostContextMenu,
	},

	props: {
		// Map object
		map: {
			type: Object,
			required: true,
		},
		mapContainerEl: {
			type: Object,
			required: true,
		},
		// Posts for show on map
		posts: {
			type: Array,
			required: true,
			default: new Array(),
		},
	},

	setup() {
		const postsStore = usePostsStore()
	
		return {
			postsStore: postsStore,
		}
	},

	data() {
		return {
			mapMarkers: {}, // Object {post_id: Marker}
			mapMarkersOnScreen: {},
			postMarkersColor: {}, // Object {post_id: color}
			postsGeoJson: {
				type: "FeatureCollection",
				crs: {
					type: "name",
					properties: {
							name: "urn:ogc:def:crs:OGC:1.3:CRS84"
					}
				},
				features: [],
			},

			// Post context menu
			postContextMenuVisible: false,
			postContextMenuPosition: { top: 0, left: 0 },
			postForContextMenu: undefined,
		}
	},

	async created() {
		//console.log("Created posts layer ", this.posts)
		this.updatePostMarksColor()
		this.updatePostsGeoJson()

		// On change page
		this.$watch(
			() => this.posts,
			(/*newVal, oldVal*/) => {
				//console.log("Update posts list. New one is ", newVal)
				this.updatePostMarksColor()
				this.updatePostsGeoJson()
			},
			{deep: true}
		)
	},

	async mounted() {
		// Map events handlers
		//
		// Fired immediately after all necessary resources have been downloaded
		// and the first visually complete rendering of the map has occurred
		this.map.on('load', () => {
			//console.log("Posts layer loaded.")

			this.addPostMarkersLayer()
		});

		// The `click` event
		this.map.on('click', function() {
			//console.log("Click event occured")
			this.hidePostContextMenu()
		}.bind(this));

		// Right button click
		this.map.on('contextmenu', function() {
			this.hidePostContextMenu()
		}.bind(this));

		// Drag map
		this.map.on('drag', function() {
			this.hidePostContextMenu()
		}.bind(this));

		// Zoom map
		this.map.on('zoomstart', function() {
			this.hidePostContextMenu()
		}.bind(this));

		//
		this.map.on('zoomend', function() {
			//console.log('A zoomend event occurred.');
			this.renderPostMarkers()
		}.bind(this));

		// after the GeoJSON data is loaded,
		// update markers on the screen and do so on every map move/moveend
		this.map.on('data', function (e) {
			if (e.sourceId !== this.$options.POSTS_SOURCE_ID || !e.isSourceLoaded)
				return;
			//console.log(`A data update for "${this.$options.POSTS_SOURCE_ID}" event occurred.`);
			this.renderPostMarkers()
		}.bind(this));
	},

	methods: {
		addPostMarkersLayer() {
			//console.log("Posts geojson", this.postsGeoJson)
			// Push Posts data to map
			this.map.addSource(this.$options.POSTS_SOURCE_ID, {
				'type': 'geojson',
				'data': this.postsGeoJson,
				//'data': 'https://docs.maptiler.com/sdk-js/assets/earthquakes.geojson',
				cluster: true,
				clusterMaxZoom: 14, // Max zoom to cluster points on
				clusterRadius: 20 // Radius of each cluster when clustering points (defaults to 50)
			});

			// Add layer width post markers
			this.map.addLayer({
				id: 'unclustered-post',
				type: 'circle',
				source: this.$options.POSTS_SOURCE_ID,
				filter: ['==', 'is_active', true],
				paint: {
					'circle-color': '#11b4da',
					'circle-radius': 0,
					'circle-stroke-width': 1,
					'circle-stroke-color': '#fff'
				}
			});

			// Add layers with cluster markers
			this.map.addLayer({
				id: 'post-clusters',
				type: 'circle',
				source: this.$options.POSTS_SOURCE_ID,
				filter: ['has', 'point_count'],
				paint: {
					// Use step expressions (https://docs.maptiler.com/gl-style-specification/expressions/#step)
					// with three steps to implement three types of circles:
					//   * Blue, 20px circles when point count is less than 100
					//   * Yellow, 30px circles when point count is between 100 and 750
					//   * Pink, 40px circles when point count is greater than or equal to 750
					'circle-color': [
						'step',
						['get', 'point_count'],
						'#5182c9',
						100,
						'#f1f075',
						750,
						'#f28cb1'
					],
					'circle-radius': [
						'step',
						['get', 'point_count'],
						20,
						100,
						30,
						750,
						40
					]
			}
			});
			this.map.addLayer({
				id: 'post-cluster-count',
				type: 'symbol',
				source: this.$options.POSTS_SOURCE_ID,
				filter: ['has', 'point_count'],
				layout: {
					'text-field': '{point_count_abbreviated}',
					'text-font': ['Roboto', 'Arial Unicode MS Bold'],
					'text-size': 12
				}
			});
		},

		renderPostMarkers() {
            const features = this.map.querySourceFeatures(this.$options.POSTS_SOURCE_ID);
			
			// Hide all markers that shown
			for (const id of Object.keys(this.mapMarkersOnScreen)) {
				this.setPostMarkerVisibility(id, false)
			}
			this.mapMarkersOnScreen = {}
            // for every post on the screen, bind an HTML marker for it,
            // and add it to the map if it's not there already
            features.forEach((feature) => {
				//console.log("curent feature = ", feature)

                //const coords = features[i].geometry.coordinates;
                const props = feature.properties;
				// If feature is cluster
				if (props.cluster) {
					//
				} else {
					//console.log("curent post name = ", props.name)
					const id = props.id
					
					var marker = this.mapMarkers[id];
					//console.log("curent post marker = ", marker)
					if (!marker) {
						this.addPostMarker(this.postsStore.post(id))
						marker = this.mapMarkers[id];
						//console.log("Create post marker = ", marker)
					}
					
					// Update coordinates for correct position if multiple create and remove post on one session
					marker.setLngLat(this.postsStore.post(id).passport.get(Post.passportKeys.location).coordinates)
					this.mapMarkersOnScreen[id] = marker

					this.setPostMarkerVisibility(id, true)
				}
            })
		},

		addPostMarker(post) {
			//console.log("marker DOM el: ", this.$refs[post.id][0])

			// Bind marker to map coordinates
			// Add popup to marker
			// Save to array
			this.mapMarkers[post.id] = new Marker({element: this.$refs[post.id][0],})
			.setLngLat(post.passport.get(Post.passportKeys.location).coordinates)
			.addTo(this.map);
		},

		addPopupToPostMarker(marker, post) {
			const markerRadius = 10, linearOffset = 25;
			const popupOffsets = {
				'top': [0, 0],
				'top-left': [0,0],
				'top-right': [0,0],
				'bottom': [0, -this.marckHeight/2],
				'bottom-left': [linearOffset, (this.marckHeight/2 - markerRadius + linearOffset) * -1],
				'bottom-right': [-linearOffset, (this.marckHeight/2 - markerRadius + linearOffset) * -1],
				'left': [markerRadius, (this.marckHeight/2 - markerRadius) * -1],
				'right': [-markerRadius, (this.marckHeight/2 - markerRadius) * -1]
			};

			marker.setPopup(
				new Popup({
					offset: popupOffsets,
					maxWidth: "400px",
					closeButton: false,
				}).setHTML(this.postPopupHtml(post))
			)
		},

		postPopupHtml(post) {
			const name = `<span>${post.name}</span>`
			let content = ""

			// Assemble popup html
			content = `${name}`

			return content
		},

		setPostMarkerVisibility(postId, isVisible) {
			if (this.$refs[postId][0] !== undefined) {
				this.$refs[postId][0].style.visibility = isVisible ? "visible" : "hidden";
			}
		},

		postMarkerColor(/*post*/) {
			let color = "LightGreen"
			
			return color
		},

		
		updatePostsGeoJson() {
			this.postsGeoJson.features.length = 0
			this.posts.forEach(post => {
				this.postsGeoJson.features.push({
					type: "Feature",
					properties: {
						id: post.id,
						name: post.name,
						is_active: post.isActive,
						markerColor: this.postMarkerColor(post),
					},
					geometry: {
						type: "Point",
						coordinates: post.passport.get(Post.passportKeys.location).coordinates,
					},
				})
			})

			const mapSource = this.map?.getSource(this.$options.POSTS_SOURCE_ID)
			if (mapSource !== undefined) {
				//console.log("Reset GeoJson!")
				mapSource.setData(this.postsGeoJson);
			}
		},

		updatePostMarksColor() {
			this.postMarkersColor = {}

			this.posts.forEach(post => {
				this.postMarkersColor[post.id] = this.postMarkerColor(post)
			})
		},

		showPostMarkerContextMenu(event, post) {
			//console.log("map cpntainer ref", this.$refs.mapContainer.getBoundingClientRect())
			//console.log("event ", event, post.name)
			event.preventDefault();

			this.hidePostContextMenu()
			
			const rect = this.mapContainerEl.getBoundingClientRect()
			this.postContextMenuVisible = true;
			//console.log(`event.pageY=${event.pageY}, getBoundingClientRect().y=`, rect)
			this.postContextMenuPosition = {
				top: event.pageY - rect.y,
				left: event.pageX - rect.x
			};
			this.postForContextMenu = post
		},

		hidePostContextMenu() {
			this.postContextMenuVisible = false;
		},

		removePostLocation(post) {
			this.hidePostContextMenu ()

			//post.passport.set(Post.passportKeys.location, null)
			post.setLocation(0, 0)
			
			let payload = {}
			payload[Post.passportKeys.location] = post.passport.get(Post.passportKeys.location)
			//console.log("Location payload", payload)

			this.postsStore.updatePasport(async (result) => {
				if (result) {
					if (this.mapMarkers[post.id] !== undefined) {
						//await this.updatePosts()
						this.setPostMarkerVisibility(post.id, false)
					}
				} else {
					console.error("Passport update error")
				}
			}, post.id, payload)
		},

		onClickPostMarker(post) {
			//console.log("Click on post marker event occured")
			this.addPopupToPostMarker(this.mapMarkers[post.id], post)
		},
	},

	computed: {
		marckHeight() { return 63 },
		marckWidth() { return 31 },
		postObj() { return Post }
	}
}
</script>


<style scoped>
.post-marker {
	cursor: pointer;
	z-index: 1;
	visibility: hidden;
}
</style>